import React, {useContext, useEffect, useRef, useState} from "react";
import {useAtom, useSetAtom} from "jotai";
import {PhonePanelOpenAtom, PhoneTabAtom} from "./atoms";
import {
  Box, Button, CircularProgress, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Tooltip, Typography, useMediaQuery, useTheme} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {CallEnd01Icon, HeadsetIcon, Refresh01Icon} from "@hugeicons/core-free-icons";
import {SignInContext} from "../../context/SignInContext";
import {isWebrtcEnabled, useWebrtc, WEBRTC_ROLES} from "../../context/WebrtcContext";
import {useRegistryProject} from "../../pages/registry-project/RegistryProjectContext";
import {AgentGroupsSection} from "./AgentGroupsSection";
import {PhonePanel} from "../../components/webrtc/PhonePanel";
import {MiniCallBar} from "../../components/webrtc/MiniCallBar";
import {CallTimer, isCallLive} from "../../components/webrtc/ActiveCallCard";
import {useAgentQueue} from "../../hooks/useAgentQueue";
import {pbxWebSocketUrl} from "../../pages/backend-configurations/serviceFields";

const buttonSx = {
  height: 32, px: 1.5, borderRadius: "9px", textTransform: "none", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
  minWidth: 0,
};

const STATES = {
  disconnected: {label: "Connect", dot: null},
  connecting: {label: "Connecting…", dot: null},
  verifying: {label: "Checking Asterisk…", dot: null},
  registered: {label: "Connected", dot: "#22C55E"},
  unregistered: {label: "Reconnecting…", dot: "#F59E0B"},
  failed: {label: "Browser calls failed", dot: "#E5484D"},
};

// Header button that turns the browser phone (WebRTC) on and shows its state. Project ADMIN / MANAGER / AGENT only.
// Once turned on it reconnects by itself on the next login / page load (fresh config every time).
export const WebrtcConnectButton = ({role, compact = false}) => {
  const {currentUser} = useContext(SignInContext);
  const {project, subProject} = useRegistryProject();
  const {status, error, call, connect, disconnect, attachProject, detachProject} = useWebrtc();
  const live = isCallLive(call);
  const isMobile = useMediaQuery(useTheme().breakpoints.down("md"));
  const [anchor, setAnchor] = useState(null);
  // the phone panel's open tab (calls | dial | queue), null when it is closed; also opened from the mobile bottom nav
  const [phoneTab, setPhoneTab] = useAtom(PhoneTabAtom);
  useEffect(() => () => setPhoneTab(null), [setPhoneTab]);
  const setPhonePanelOpen = useSetAtom(PhonePanelOpenAtom);
  useEffect(() => {
    setPhonePanelOpen(Boolean(phoneTab));
    return () => setPhonePanelOpen(false);
  }, [phoneTab, setPhonePanelOpen]);
  const allowed = WEBRTC_ROLES.includes(role);
  const queue = useAgentQueue({
    projectId: project?.id, enabled: allowed && status === "registered", detailed: phoneTab === "queue",
  });
  // the browser phone connects to the project's backend configuration PBX; without one it cannot be turned on
  const wsUrl = pbxWebSocketUrl(project?.backendConfiguration?.pbx);
  // subProjects: names for the redirect dialog's bot extensions
  const projectInfo = {projectId: project?.id, subProjectId: subProject?.id, wsUrl, subProjects: project?.subProjects || []};

  useEffect(() => {
    if (!allowed) return;
    attachProject(projectInfo);
    // leaving the project (or another one opened) turns its phone off: back to "Connect"
    return () => detachProject(project?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, project?.id, subProject?.id, wsUrl]);

  // reconnect once per project when the user turned it on there before (page reload)
  const autoConnectedRef = useRef(null);
  useEffect(() => {
    if (!allowed || !project?.id || autoConnectedRef.current === project.id || !wsUrl || status !== "disconnected") return;
    autoConnectedRef.current = project.id;
    if (isWebrtcEnabled(currentUser?.username, project.id)) connect(projectInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, project?.id, wsUrl, currentUser?.username, status]);

  // answering a call opens the phone on its active call card (on mobile the bottom navigation opens it)
  useEffect(() => {
    if (live && !isMobile) setPhoneTab(current => current || "calls");
  }, [live]);

  // turned off: the panel has nothing left to call with; a short "Reconnecting…" keeps it open
  useEffect(() => {
    if (status === "disconnected") setPhoneTab(null);
  }, [status]);

  if (!allowed) return null;

  const state = STATES[status] || STATES.disconnected;
  const connected = status === "registered";

  // no PBX in the project's backend configuration: nothing to connect to
  const noPbx = !wsUrl && status === "disconnected";

  const onClick = (e) => {
    if (status === "disconnected") connect(projectInfo);
    else if (connected) setPhoneTab(current => current ? null : "calls");
    else setAnchor(e.currentTarget);
  };

  return (
    <>
      <Tooltip title={noPbx ? "No PBX is set in this project's backend configuration" : ""}>
      <span>
      <Button
        onClick={onClick}
        disabled={noPbx || status === "connecting" || status === "verifying"}
        aria-label={state.label}
        startIcon={status === "connecting" || status === "verifying"
          ? <CircularProgress size={14} sx={{color: "#4B55C9"}}/>
          : <HugeiconsIcon icon={HeadsetIcon} size={15}/>}
        sx={{
          ...buttonSx, border: "1px solid #E4E4EE", bgcolor: "#fff", "&:hover": {bgcolor: "#F4F4FA"},
          color: status === "registered" ? "#1E8A55" : status === "failed" ? "#C73A3A" : "#23264D",
          ...(connected && !compact ? {border: 0, bgcolor: live ? "rgba(47, 194, 117, 0.22)" : "rgba(47, 194, 117, 0.12)", fontVariantNumeric: "tabular-nums", "&:hover": {bgcolor: "rgba(47, 194, 117, 0.22)"}} : {}),
          "&.Mui-disabled": {color: noPbx ? "#A0A0B4" : "#4B55C9"},
          ...(compact ? {width: 32, px: 0, "& .MuiButton-startIcon": {m: 0}} : {}),
        }}
      >
        {compact ? null : connected ? (live ? <>On a call · <CallTimer startedAt={call.startedAt}/></> : queue.count > 0 ? `Available · ${queue.count} waiting` : "Available") : state.label}
        {state.dot && (
          <Box component={"span"} sx={{
            width: 7, height: 7, borderRadius: "50%", bgcolor: state.dot,
            ...(compact ? {position: "absolute", top: 5, right: 5} : {ml: 1}),
          }}/>
        )}
      </Button>
      </span>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        // "Call a number" opens a dialog: focus goes to its phone field, not back to this button
        disableRestoreFocus
        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
        transformOrigin={{vertical: "top", horizontal: "right"}}
        PaperProps={{sx: {borderRadius: "12px", mt: 0.75, width: connected ? 340 : "auto", minWidth: 240, maxWidth: "calc(100vw - 32px)", boxShadow: "0 12px 40px rgba(35, 38, 77, 0.16)"}}}
      >
        <Stack sx={{px: 2, py: 1}}>
          <Typography sx={{fontSize: 13, fontWeight: 700, color: "#23264D"}}>{state.label}</Typography>
          <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
            {status === "registered"
              ? "Asterisk is connected to your browser phone. You can take over and place calls."
              : error || "Your browser phone is not registered."}
          </Typography>
        </Stack>
        <Divider sx={{my: 0.5}}/>
        {status !== "registered" && (
          <MenuItem onClick={() => {
            setAnchor(null);
            connect(projectInfo);
          }} sx={{mx: 0.75, borderRadius: "8px", gap: 1}}>
            <ListItemIcon sx={{minWidth: "28px !important"}}><HugeiconsIcon icon={Refresh01Icon} size={18}/></ListItemIcon>
            <ListItemText primary={"Reconnect"} primaryTypographyProps={{fontSize: 13, fontWeight: 600}}/>
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          setAnchor(null);
          disconnect();
        }} sx={{mx: 0.75, borderRadius: "8px", gap: 1, color: "#C73A3A"}}>
          <ListItemIcon sx={{minWidth: "28px !important", color: "#C73A3A"}}><HugeiconsIcon icon={CallEnd01Icon} size={18}/></ListItemIcon>
          <ListItemText primary={"Turn off browser calls"} primaryTypographyProps={{fontSize: 13, fontWeight: 600}}/>
        </MenuItem>
      </Menu>
      {live && !phoneTab && !isMobile && <MiniCallBar onExpand={() => setPhoneTab("calls")}/>}
      {phoneTab && (
        <PhonePanel
          tab={phoneTab} onChangeTab={setPhoneTab} onClose={() => setPhoneTab(null)} queue={queue}
          settings={(
            <Stack>
              <Stack sx={{px: 2, py: 1.5}}>
                <Typography sx={{fontSize: 13, fontWeight: 700, color: "#23264D"}}>{state.label}</Typography>
                <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
                  Asterisk is connected to your browser phone. You can take over and place calls.
                </Typography>
              </Stack>
              <AgentGroupsSection role={role} onNavigate={() => setPhoneTab(null)}/>
              <MenuItem onClick={disconnect} sx={{mx: 0.75, my: 1, borderRadius: "8px", gap: 1, color: "#C73A3A"}}>
                <ListItemIcon sx={{minWidth: "28px !important", color: "#C73A3A"}}><HugeiconsIcon icon={CallEnd01Icon} size={18}/></ListItemIcon>
                <ListItemText primary={"Turn off browser calls"} primaryTypographyProps={{fontSize: 13, fontWeight: 600}}/>
              </MenuItem>
            </Stack>
          )}
        />
      )}
    </>
  );
};
